export const translations = {
  EN: {
    nav: { services: "What we build", industries: "Industries", pricing: "Pricing", blog: "Blog", contact: "Contact", bookCall: "Book a Call", portal: "Client portal", admin: "Admin" },
    hero: {
      eyebrow: "Web & App Development — Dubai · Sharjah · Deira",
      l1: "Can your customers",
      l2: "find you?",
      sub: "We build websites and apps that make your business easy to find, easy to understand and easy to contact — in Dubai, Sharjah and across the UAE.",
      cta1: "Book a Call",
      cta2: "See pricing",
      scroll: "Scroll",
    },
    reveal: {
      eyebrow: "The difference we make",
      title: "Invisible, or impossible to miss.",
      sub: "Drag the handle. This is what your customers experience when they search for a business like yours.",
      before: "Before",
      after: "After",
      searchQuery: "café near me · Dubai Marina",
      beforeItems: ["No website found", "Unverified listing", "No photos, no menu", "Hours unknown"],
      afterName: "Café Lumière",
      afterTags: ["Open now · until 23:00", "Menu · Reservations", "WhatsApp · Directions"],
      afterCta: "Visit website",
      disclaimer: "Illustrative example — not a real search result. We never guarantee search rankings.",
    },
    manifesto: {
      eyebrow: "Why Bezalel Visionary",
      chapters: [
        { n: "01", title: "Your business exists. Make it visible.", body: "Your future customers search on their phones — for a table, a treatment, a room, a service. If nothing clear appears, they choose whoever does appear. Not because they're better. Because they're findable." },
        { n: "02", title: "Credibility is judged in seconds.", body: "An outdated site, a broken mobile page or no site at all creates doubt before you've said a word. A clear, current presence answers the questions people actually have: what you offer, where you are, how to reach you." },
        { n: "03", title: "Your presence should work for you.", body: "Changing your hours, your menu or your prices shouldn't require a developer. We build presences you can keep current — so your online business matches your real one." },
      ],
    },
    industries: {
      eyebrow: "Who we work with",
      title: "Built for the businesses that run this city.",
      sub: "Every sector has a different visibility problem. We solve the one that's yours.",
      items: [
        { key: "restaurants", name: "Restaurants & Cafés", problem: "Menus locked in PDFs, outdated hours and no way to book cost you covers every day.", answer: "Menu, hours, location and reservations — front and centre, always current." },
        { key: "wellness", name: "Wellness & Medical", problem: "Patients research credibility before they ever call. A weak presence loses them at the first search.", answer: "Treatments, practitioners and booking enquiries presented with the clarity patients expect." },
        { key: "hotels", name: "Hotels & Hospitality", problem: "Guests decide from their phones — and OTAs take a cut when you can't take direct enquiries.", answer: "Rooms, offers and a direct path to book or ask, without the clutter." },
        { key: "nightlife", name: "Nightlife", problem: "Events change weekly. A website that lags a season behind might as well be closed.", answer: "Events, tables and guest lists you can update in minutes." },
        { key: "independent", name: "Independent Businesses", problem: "You're recommended by word of mouth — then people search for you and find nothing.", answer: "A clear presence that confirms the recommendation and makes contact effortless." },
      ],
    },
    build: {
      eyebrow: "What we build",
      title: "Three ways to become findable.",
      items: [
        { name: "Websites", body: "Fast, mobile-first websites that present what you offer clearly and turn visitors into enquiries." },
        { name: "Web & mobile applications", body: "Booking, ordering, client areas — applications built around how your business actually works." },
        { name: "Digital presence", body: "A coherent presence across your site, profiles and listings, so customers get the same clear answer everywhere." },
      ],
    },
    process: {
      eyebrow: "How it works",
      title: "Three steps. No jargon.",
      steps: [
        { n: "1", title: "We listen", body: "We learn your business, your customers and your goals — in English, French or Portuguese." },
        { n: "2", title: "We build", body: "We design and build your presence. You review and approve at every stage." },
        { n: "3", title: "You go live", body: "We launch, show you how to manage it and stay available as you grow." },
      ],
    },
    pricing: {
      eyebrow: "Pricing",
      title: "Clear plans, in AED.",
      sub: "Monthly plans for an ongoing presence, or a one-time build if you prefer to own it outright.",
      perMonth: "AED / month",
      placeholder: "Final inclusions pending business approval",
      placeholderItem: "Inclusion to be confirmed",
      plans: [
        { key: "essentiel", name: "Essentiel", price: "450", tag: "Get found", desc: "For businesses that need a professional presence, fast." },
        { key: "croissance", name: "Croissance", price: "850", tag: "Most popular", desc: "For businesses ready to turn visibility into steady enquiries.", featured: true },
        { key: "premium", name: "Premium", price: "1,500", tag: "Full presence", desc: "For businesses that want their digital presence fully managed." },
      ],
      oneTime: { title: "One-time build", body: "Prefer a single payment? We scope and quote your project individually.", cta: "Request a quote" },
      alacarte: { title: "À la carte", body: "Individual services — pages, updates, listings — priced per need.", cta: "Ask what's possible" },
      note: "Prices are indicative. Exact inclusions are confirmed before any commitment.",
      viewFull: "View full pricing",
      cta: "Discuss your project",
    },
    trust: {
      title: "Proof, not promises.",
      body: "Client results, reviews and case studies will appear here as they are verified — we don't publish invented numbers.",
    },
    finalCta: {
      title: "So — can your customers find you?",
      body: "Tell us about your business. We'll tell you exactly what it would take to make it visible.",
      cta1: "Book a Call",
      cta2: "Start a project",
    },
    marquee: ["Dubai", "Sharjah", "Deira", "Be found", "Be understood", "Be chosen", "EN · FR · PT"],
    footer: {
      tagline: "Websites and apps for businesses in Dubai, Sharjah, Deira and across the UAE.",
      nav: "Navigate", services: "Sectors", legalTitle: "Legal",
      legal: "Bezalel Visionary operates through a registered French entity. A UAE establishment is planned.",
      rights: "All rights reserved.",
      portal: "Client portal",
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's make your business visible.",
      sub: "A short call is the fastest way to know what your business needs. No obligation, no jargon.",
      name: "Your name", businessName: "Business name", email: "Email", phone: "Phone / WhatsApp",
      businessType: "Business type", location: "Location",
      websiteStatus: "Current website", needs: "What do you need?",
      preferredLanguage: "Preferred language", message: "Message",
      messagePh: "Tell us briefly about your business…",
      businessTypes: ["Restaurant / Café", "Wellness / Medical", "Hotel / Hospitality", "Nightlife", "Independent business", "Other"],
      locations: ["Dubai", "Sharjah", "Deira", "Abu Dhabi", "Other UAE", "Outside UAE"],
      websiteOptions: ["No website yet", "Outdated website", "Website I can't manage", "Website that works, needs more"],
      needsOptions: ["Website", "Web / mobile app", "Digital presence", "Not sure yet"],
      submit: "Book a Call",
      sending: "Sending…",
      successTitle: "Thank you — we've received your enquiry.",
      successBody: "We'll get back to you within one business day, in your preferred language.",
      another: "Send another enquiry",
      required: "Please fill in your name and a valid email.",
      error: "Something went wrong. Please try again.",
      select: "Select…",
    },
    login: {
      clientTitle: "Client portal",
      adminTitle: "Admin portal",
      sub: "Sign in to follow your project, invoices and requests.",
      adminSub: "Restricted access.",
      email: "Email", password: "Password", submit: "Sign in", signingIn: "Signing in…",
      demo: "Demo access: client@demo.com / Client@Demo2026",
      back: "Back to site",
    },
    portal: {
      welcome: "Welcome back",
      project: "Project status",
      stages: { concept: "Concept", revision: "Révision", validation: "Validation", delivered: "Livré" },
      updated: "Last updated",
      visibility: "Visibility",
      visibilityEmpty: "Approved visibility statistics will appear here once verified. We only show real numbers.",
      finance: "Finance",
      invoice: "Invoice", amount: "Amount", status: "Status", due: "Due",
      paid: "Paid", dueStatus: "Due",
      requests: "Requests & revisions",
      newRequest: "New request",
      kind: "Type", request: "Request", revision: "Revision",
      subject: "Subject", messageLabel: "Message", send: "Send", cancel: "Cancel",
      open: "Open", inProgress: "In progress", resolved: "Resolved",
      empty: "No requests yet. Need a change? Send us a request.",
      logout: "Sign out",
      requestSent: "Request sent — we'll take it from here.",
    },
    admin: {
      title: "Admin dashboard",
      overview: "Overview",
      stats: { new_enquiries: "New enquiries", clients: "Clients", active_projects: "Active projects", open_requests: "Open requests" },
      tabs: { enquiries: "Enquiries", clients: "Clients & projects", requests: "Requests" },
      enquiryCols: { name: "Name", business: "Business", contact: "Contact", type: "Type", lang: "Lang", status: "Status", received: "Received" },
      statuses: { new: "New", contacted: "Contacted", closed: "Closed" },
      projectStage: "Project stage",
      noEnquiries: "No enquiries yet.",
      logout: "Sign out",
    },
    login2: { welcome: "Welcome back", subtitle: "Access your Bezalel Visionary client space.", google: "Continue with Google", or: "or", magic: "Email me a sign-in link", magicSent: "Check your inbox — your secure sign-in link is on its way.", magicInvalid: "This sign-in link is invalid or has expired.", usePassword: "Use email & password instead", useMagic: "Use a sign-in link instead", forgot: "Forgot password?", resetSent: "If this email exists, a reset link has been sent.", resetTitle: "Choose a new password", newPassword: "New password (min. 8 characters)", resetSubmit: "Reset password", resetDone: "Password updated — you can sign in now.", emailPh: "you@business.com" },
    payment: { checking: "Confirming your payment…", success: "Payment confirmed — thank you!", receipt: "Your invoice is now marked as paid in the Finance section.", failed: "We couldn't confirm this payment yet. If you were charged, it will update shortly.", back: "Back to portal" },
    work: { eyebrow: "Selected work", title: "Work that makes businesses findable.", sub: "Real projects we have designed and built — platforms, web apps and websites.", demoTag: "Demo preview", visit: "Visit website", back: "All projects" },
    blog: { eyebrow: "Journal", title: "News & insights", sub: "Practical notes on visibility, websites and digital presence for businesses in the UAE.", readMore: "Read article", back: "All articles", empty: "No articles published yet." },
    faqT: { faqTitle: "Frequently asked questions", testimonialsTitle: "What clients say", testimonialsEmpty: "Verified client testimonials will appear here as projects are delivered — we never publish invented reviews." },
    portal2: { messages: "Messages", noMessages: "No messages yet. Write to us below.", typeMessage: "Write a message…", send: "Send", deliverables: "Deliverables & files", noDeliverables: "Deliverables will appear here as they're ready.", updates: "Important updates", payNow: "Pay now", receipt: "Receipt (PDF)", contactTitle: "Contact & meetings", contactBody: "Need to talk about your project? Reach us directly — we reply in your language.", emailUs: "Email us", whatsapp: "WhatsApp", open: "Open" },
    misc: { loading: "Loading…", flagLogo: "Simplified navigation mark pending official brand asset." },
  },

  FR: {
    nav: { services: "Ce que nous créons", industries: "Secteurs", pricing: "Tarifs", blog: "Journal", contact: "Contact", bookCall: "Réserver un appel", portal: "Espace client", admin: "Admin" },
    hero: {
      eyebrow: "Création web & apps — Dubaï · Sharjah · Deira",
      l1: "Vos clients",
      l2: "vous trouvent-ils ?",
      sub: "Nous créons des sites et des applications qui rendent votre entreprise facile à trouver, à comprendre et à contacter — à Dubaï, Sharjah et dans tous les Émirats.",
      cta1: "Réserver un appel",
      cta2: "Voir les tarifs",
      scroll: "Défiler",
    },
    reveal: {
      eyebrow: "La différence que nous faisons",
      title: "Invisible, ou impossible à manquer.",
      sub: "Faites glisser le curseur. Voici ce que vivent vos clients quand ils cherchent une entreprise comme la vôtre.",
      before: "Avant",
      after: "Après",
      searchQuery: "café près de moi · Dubai Marina",
      beforeItems: ["Aucun site trouvé", "Fiche non vérifiée", "Ni photos, ni menu", "Horaires inconnus"],
      afterName: "Café Lumière",
      afterTags: ["Ouvert · jusqu'à 23h00", "Menu · Réservations", "WhatsApp · Itinéraire"],
      afterCta: "Voir le site",
      disclaimer: "Exemple illustratif — pas un vrai résultat de recherche. Nous ne garantissons jamais de classement.",
    },
    manifesto: {
      eyebrow: "Pourquoi Bezalel Visionary",
      chapters: [
        { n: "01", title: "Votre entreprise existe. Rendez-la visible.", body: "Vos futurs clients cherchent sur leur téléphone — une table, un soin, une chambre, un service. Si rien de clair n'apparaît, ils choisissent ceux qui apparaissent. Pas parce qu'ils sont meilleurs. Parce qu'ils sont trouvables." },
        { n: "02", title: "La crédibilité se joue en quelques secondes.", body: "Un site daté, une page mobile cassée ou aucun site du tout crée le doute avant même le premier échange. Une présence claire et à jour répond aux vraies questions : ce que vous proposez, où vous êtes, comment vous joindre." },
        { n: "03", title: "Votre présence doit travailler pour vous.", body: "Changer vos horaires, votre menu ou vos prix ne devrait pas exiger un développeur. Nous créons des présences que vous pouvez tenir à jour — pour que votre vitrine en ligne reflète votre réalité." },
      ],
    },
    industries: {
      eyebrow: "Nos clients",
      title: "Conçu pour les entreprises qui font vivre la ville.",
      sub: "Chaque secteur a son propre problème de visibilité. Nous résolvons le vôtre.",
      items: [
        { key: "restaurants", name: "Restaurants & Cafés", problem: "Des menus en PDF, des horaires dépassés et aucune réservation en ligne vous coûtent des couverts chaque jour.", answer: "Menu, horaires, adresse et réservations — au premier plan, toujours à jour." },
        { key: "wellness", name: "Bien-être & Médical", problem: "Les patients vérifient votre crédibilité avant d'appeler. Une présence faible les perd dès la première recherche.", answer: "Soins, praticiens et demandes de rendez-vous présentés avec la clarté attendue." },
        { key: "hotels", name: "Hôtels & Hospitalité", problem: "Les voyageurs décident depuis leur téléphone — et les plateformes prennent leur commission quand la demande directe est impossible.", answer: "Chambres, offres et un chemin direct pour réserver ou demander, sans encombrement." },
        { key: "nightlife", name: "Vie nocturne", problem: "Les événements changent chaque semaine. Un site en retard d'une saison, c'est comme une porte fermée.", answer: "Événements, tables et guest lists modifiables en quelques minutes." },
        { key: "independent", name: "Indépendants", problem: "On vous recommande de bouche à oreille — puis on vous cherche en ligne et on ne trouve rien.", answer: "Une présence claire qui confirme la recommandation et rend le contact évident." },
      ],
    },
    build: {
      eyebrow: "Ce que nous créons",
      title: "Trois façons de devenir trouvable.",
      items: [
        { name: "Sites web", body: "Des sites rapides, pensés mobile d'abord, qui présentent clairement votre offre et transforment les visites en demandes." },
        { name: "Applications web & mobiles", body: "Réservation, commande, espaces clients — des applications construites autour de votre fonctionnement réel." },
        { name: "Présence digitale", body: "Une présence cohérente sur votre site, vos profils et vos fiches, pour une même réponse claire partout." },
      ],
    },
    process: {
      eyebrow: "Comment ça marche",
      title: "Trois étapes. Zéro jargon.",
      steps: [
        { n: "1", title: "Nous écoutons", body: "Nous comprenons votre entreprise, vos clients et vos objectifs — en français, anglais ou portugais." },
        { n: "2", title: "Nous créons", body: "Nous concevons et construisons votre présence. Vous validez à chaque étape." },
        { n: "3", title: "Vous êtes en ligne", body: "Nous lançons, vous montrons comment gérer, et restons disponibles pour la suite." },
      ],
    },
    pricing: {
      eyebrow: "Tarifs",
      title: "Des formules claires, en AED.",
      sub: "Des formules mensuelles pour une présence suivie, ou une création en paiement unique si vous préférez.",
      perMonth: "AED / mois",
      placeholder: "Contenu final en attente de validation",
      placeholderItem: "Inclusion à confirmer",
      plans: [
        { key: "essentiel", name: "Essentiel", price: "450", tag: "Être trouvé", desc: "Pour les entreprises qui ont besoin d'une présence professionnelle, vite." },
        { key: "croissance", name: "Croissance", price: "850", tag: "Le plus choisi", desc: "Pour transformer la visibilité en demandes régulières.", featured: true },
        { key: "premium", name: "Premium", price: "1 500", tag: "Présence complète", desc: "Pour une présence digitale entièrement gérée pour vous." },
      ],
      oneTime: { title: "Paiement unique", body: "Vous préférez un paiement unique ? Nous chiffrons votre projet individuellement.", cta: "Demander un devis" },
      alacarte: { title: "À la carte", body: "Des services à l'unité — pages, mises à jour, fiches — facturés au besoin.", cta: "Voir ce qui est possible" },
      note: "Prix indicatifs. Les inclusions exactes sont confirmées avant tout engagement.",
      viewFull: "Voir tous les tarifs",
      cta: "Parler de votre projet",
    },
    trust: {
      title: "Des preuves, pas des promesses.",
      body: "Résultats clients, avis et études de cas apparaîtront ici une fois vérifiés — nous ne publions pas de chiffres inventés.",
    },
    finalCta: {
      title: "Alors — vos clients vous trouvent-ils ?",
      body: "Parlez-nous de votre entreprise. Nous vous dirons exactement ce qu'il faut pour la rendre visible.",
      cta1: "Réserver un appel",
      cta2: "Lancer un projet",
    },
    marquee: ["Dubaï", "Sharjah", "Deira", "Être trouvé", "Être compris", "Être choisi", "FR · EN · PT"],
    footer: {
      tagline: "Sites et applications pour les entreprises de Dubaï, Sharjah, Deira et de tous les Émirats.",
      nav: "Navigation", services: "Secteurs", legalTitle: "Mentions",
      legal: "Bezalel Visionary opère via une entité française enregistrée. Un établissement aux Émirats est prévu.",
      rights: "Tous droits réservés.",
      portal: "Espace client",
    },
    contact: {
      eyebrow: "Contact",
      title: "Rendons votre entreprise visible.",
      sub: "Un court appel est le moyen le plus rapide de savoir ce qu'il faut à votre entreprise. Sans engagement, sans jargon.",
      name: "Votre nom", businessName: "Nom de l'entreprise", email: "Email", phone: "Téléphone / WhatsApp",
      businessType: "Type d'activité", location: "Localisation",
      websiteStatus: "Site actuel", needs: "De quoi avez-vous besoin ?",
      preferredLanguage: "Langue préférée", message: "Message",
      messagePh: "Parlez-nous brièvement de votre entreprise…",
      businessTypes: ["Restaurant / Café", "Bien-être / Médical", "Hôtel / Hospitalité", "Vie nocturne", "Indépendant", "Autre"],
      locations: ["Dubaï", "Sharjah", "Deira", "Abu Dhabi", "Autre Émirats", "Hors Émirats"],
      websiteOptions: ["Pas encore de site", "Site dépassé", "Site que je ne peux pas gérer", "Site correct, besoin de plus"],
      needsOptions: ["Site web", "Application web / mobile", "Présence digitale", "Pas encore sûr"],
      submit: "Réserver un appel",
      sending: "Envoi…",
      successTitle: "Merci — votre demande est bien reçue.",
      successBody: "Nous revenons vers vous sous un jour ouvré, dans votre langue.",
      another: "Envoyer une autre demande",
      required: "Merci d'indiquer votre nom et un email valide.",
      error: "Une erreur est survenue. Merci de réessayer.",
      select: "Choisir…",
    },
    login: {
      clientTitle: "Espace client",
      adminTitle: "Portail admin",
      sub: "Connectez-vous pour suivre votre projet, vos factures et vos demandes.",
      adminSub: "Accès restreint.",
      email: "Email", password: "Mot de passe", submit: "Se connecter", signingIn: "Connexion…",
      demo: "Accès démo : client@demo.com / Client@Demo2026",
      back: "Retour au site",
    },
    portal: {
      welcome: "Bon retour",
      project: "Statut du projet",
      stages: { concept: "Concept", revision: "Révision", validation: "Validation", delivered: "Livré" },
      updated: "Dernière mise à jour",
      visibility: "Visibilité",
      visibilityEmpty: "Les statistiques de visibilité approuvées apparaîtront ici une fois vérifiées. Nous ne montrons que des chiffres réels.",
      finance: "Finance",
      invoice: "Facture", amount: "Montant", status: "Statut", due: "Échéance",
      paid: "Payée", dueStatus: "À payer",
      requests: "Demandes & révisions",
      newRequest: "Nouvelle demande",
      kind: "Type", request: "Demande", revision: "Révision",
      subject: "Objet", messageLabel: "Message", send: "Envoyer", cancel: "Annuler",
      open: "Ouverte", inProgress: "En cours", resolved: "Résolue",
      empty: "Aucune demande pour le moment. Besoin d'un changement ? Envoyez-nous une demande.",
      logout: "Se déconnecter",
      requestSent: "Demande envoyée — nous prenons le relais.",
    },
    admin: {
      title: "Tableau de bord admin",
      overview: "Vue d'ensemble",
      stats: { new_enquiries: "Nouvelles demandes", clients: "Clients", active_projects: "Projets actifs", open_requests: "Requêtes ouvertes" },
      tabs: { enquiries: "Demandes", clients: "Clients & projets", requests: "Requêtes" },
      enquiryCols: { name: "Nom", business: "Entreprise", contact: "Contact", type: "Type", lang: "Langue", status: "Statut", received: "Reçue" },
      statuses: { new: "Nouvelle", contacted: "Contactée", closed: "Fermée" },
      projectStage: "Étape du projet",
      noEnquiries: "Aucune demande pour le moment.",
      logout: "Se déconnecter",
    },
    login2: { welcome: "Bon retour", subtitle: "Accédez à votre espace client Bezalel Visionary.", google: "Continuer avec Google", or: "ou", magic: "Recevoir un lien de connexion", magicSent: "Vérifiez votre boîte mail — votre lien sécurisé arrive.", magicInvalid: "Ce lien de connexion est invalide ou expiré.", usePassword: "Utiliser email & mot de passe", useMagic: "Utiliser un lien de connexion", forgot: "Mot de passe oublié ?", resetSent: "Si cet email existe, un lien de réinitialisation a été envoyé.", resetTitle: "Choisissez un nouveau mot de passe", newPassword: "Nouveau mot de passe (min. 8 caractères)", resetSubmit: "Réinitialiser", resetDone: "Mot de passe mis à jour — vous pouvez vous connecter.", emailPh: "vous@entreprise.com" },
    payment: { checking: "Confirmation du paiement…", success: "Paiement confirmé — merci !", receipt: "Votre facture est désormais marquée comme payée dans la section Finance.", failed: "Impossible de confirmer ce paiement pour l'instant. Si vous avez été débité, le statut sera mis à jour sous peu.", back: "Retour à l'espace client" },
    work: { eyebrow: "Réalisations", title: "Des créations qui rendent les entreprises trouvables.", sub: "Des projets réels que nous avons conçus et développés — plateformes, applications web et sites.", demoTag: "Aperçu démo", visit: "Voir le site", back: "Toutes les réalisations" },
    blog: { eyebrow: "Journal", title: "Actualités & conseils", sub: "Des notes pratiques sur la visibilité, les sites web et la présence digitale des entreprises aux Émirats.", readMore: "Lire l'article", back: "Tous les articles", empty: "Aucun article publié pour le moment." },
    faqT: { faqTitle: "Questions fréquentes", testimonialsTitle: "Ce que disent nos clients", testimonialsEmpty: "Les témoignages vérifiés apparaîtront ici au fil des projets livrés — nous ne publions jamais de faux avis." },
    portal2: { messages: "Messages", noMessages: "Pas encore de messages. Écrivez-nous ci-dessous.", typeMessage: "Écrire un message…", send: "Envoyer", deliverables: "Livrables & fichiers", noDeliverables: "Les livrables apparaîtront ici dès qu'ils seront prêts.", updates: "Mises à jour importantes", payNow: "Payer", receipt: "Reçu (PDF)", contactTitle: "Contact & rendez-vous", contactBody: "Besoin d'échanger sur votre projet ? Contactez-nous directement — nous répondons dans votre langue.", emailUs: "Nous écrire", whatsapp: "WhatsApp", open: "Ouvrir" },
    misc: { loading: "Chargement…", flagLogo: "Marque de navigation simplifiée en attente de l'asset officiel." },
  },

  PT: {
    nav: { services: "O que criamos", industries: "Setores", pricing: "Preços", blog: "Blog", contact: "Contacto", bookCall: "Agendar chamada", portal: "Área do cliente", admin: "Admin" },
    hero: {
      eyebrow: "Criação de sites & apps — Dubai · Sharjah · Deira",
      l1: "Os seus clientes",
      l2: "encontram você?",
      sub: "Criamos sites e aplicações que tornam o seu negócio fácil de encontrar, de entender e de contactar — em Dubai, Sharjah e em todos os Emirados.",
      cta1: "Agendar chamada",
      cta2: "Ver preços",
      scroll: "Rolar",
    },
    reveal: {
      eyebrow: "A diferença que fazemos",
      title: "Invisível, ou impossível de ignorar.",
      sub: "Arraste o cursor. É isto que os seus clientes vivem quando procuram um negócio como o seu.",
      before: "Antes",
      after: "Depois",
      searchQuery: "café perto de mim · Dubai Marina",
      beforeItems: ["Nenhum site encontrado", "Perfil não verificado", "Sem fotos, sem menu", "Horário desconhecido"],
      afterName: "Café Lumière",
      afterTags: ["Aberto · até 23h00", "Menu · Reservas", "WhatsApp · Direções"],
      afterCta: "Ver o site",
      disclaimer: "Exemplo ilustrativo — não é um resultado de pesquisa real. Nunca garantimos posições de pesquisa.",
    },
    manifesto: {
      eyebrow: "Porquê Bezalel Visionary",
      chapters: [
        { n: "01", title: "O seu negócio existe. Torne-o visível.", body: "Os seus futuros clientes pesquisam no telemóvel — uma mesa, um tratamento, um quarto, um serviço. Se nada claro aparece, escolhem quem aparece. Não porque são melhores. Porque são encontráveis." },
        { n: "02", title: "A credibilidade decide-se em segundos.", body: "Um site desatualizado, uma página móvel quebrada ou nenhum site cria dúvida antes da primeira conversa. Uma presença clara e atual responde às perguntas reais: o que oferece, onde está, como o contactar." },
        { n: "03", title: "A sua presença deve trabalhar por si.", body: "Mudar horários, menu ou preços não devia exigir um programador. Construímos presenças que você mantém atualizadas — para que o seu negócio online reflita o real." },
      ],
    },
    industries: {
      eyebrow: "Com quem trabalhamos",
      title: "Feito para os negócios que movem a cidade.",
      sub: "Cada setor tem um problema de visibilidade diferente. Resolvemos o seu.",
      items: [
        { key: "restaurants", name: "Restaurantes & Cafés", problem: "Menus em PDF, horários desatualizados e nenhuma reserva online custam-lhe mesas todos os dias.", answer: "Menu, horários, localização e reservas — em destaque, sempre atuais." },
        { key: "wellness", name: "Bem-estar & Saúde", problem: "Os pacientes verificam a credibilidade antes de ligar. Uma presença fraca perde-os na primeira pesquisa.", answer: "Tratamentos, profissionais e pedidos de marcação apresentados com a clareza esperada." },
        { key: "hotels", name: "Hotéis & Hospitalidade", problem: "Os hóspedes decidem pelo telemóvel — e as plataformas cobram comissão quando o pedido direto é impossível.", answer: "Quartos, ofertas e um caminho direto para reservar ou perguntar, sem ruído." },
        { key: "nightlife", name: "Vida noturna", problem: "Os eventos mudam toda a semana. Um site atrasado uma temporada é como uma porta fechada.", answer: "Eventos, mesas e guest lists atualizáveis em minutos." },
        { key: "independent", name: "Negócios independentes", problem: "Recomendam-no de boca em boca — depois pesquisam por si e não encontram nada.", answer: "Uma presença clara que confirma a recomendação e torna o contacto imediato." },
      ],
    },
    build: {
      eyebrow: "O que criamos",
      title: "Três formas de ser encontrado.",
      items: [
        { name: "Sites", body: "Sites rápidos, pensados primeiro para mobile, que apresentam a sua oferta com clareza e transformam visitas em pedidos." },
        { name: "Aplicações web & mobile", body: "Reservas, pedidos, áreas de cliente — aplicações construídas à volta de como o seu negócio funciona." },
        { name: "Presença digital", body: "Uma presença coerente no site, perfis e listagens, para que os clientes recebam a mesma resposta clara em todo o lado." },
      ],
    },
    process: {
      eyebrow: "Como funciona",
      title: "Três passos. Zero jargão.",
      steps: [
        { n: "1", title: "Ouvimos", body: "Conhecemos o seu negócio, os seus clientes e os seus objetivos — em português, francês ou inglês." },
        { n: "2", title: "Construímos", body: "Desenhamos e construímos a sua presença. Você revê e aprova em cada etapa." },
        { n: "3", title: "Está no ar", body: "Lançamos, mostramos como gerir e ficamos disponíveis enquanto cresce." },
      ],
    },
    pricing: {
      eyebrow: "Preços",
      title: "Planos claros, em AED.",
      sub: "Planos mensais para uma presença contínua, ou um projeto de pagamento único se preferir.",
      perMonth: "AED / mês",
      placeholder: "Conteúdo final pendente de aprovação",
      placeholderItem: "Inclusão a confirmar",
      plans: [
        { key: "essentiel", name: "Essentiel", price: "450", tag: "Ser encontrado", desc: "Para negócios que precisam de uma presença profissional, rápido." },
        { key: "croissance", name: "Croissance", price: "850", tag: "Mais escolhido", desc: "Para transformar visibilidade em pedidos regulares.", featured: true },
        { key: "premium", name: "Premium", price: "1.500", tag: "Presença completa", desc: "Para uma presença digital totalmente gerida por nós." },
      ],
      oneTime: { title: "Pagamento único", body: "Prefere um pagamento único? Orçamentamos o seu projeto individualmente.", cta: "Pedir orçamento" },
      alacarte: { title: "À la carte", body: "Serviços individuais — páginas, atualizações, listagens — cobrados por necessidade.", cta: "Ver o que é possível" },
      note: "Preços indicativos. As inclusões exatas são confirmadas antes de qualquer compromisso.",
      viewFull: "Ver todos os preços",
      cta: "Falar do seu projeto",
    },
    trust: {
      title: "Provas, não promessas.",
      body: "Resultados de clientes, avaliações e casos de estudo aparecerão aqui quando verificados — não publicamos números inventados.",
    },
    finalCta: {
      title: "Então — os seus clientes encontram você?",
      body: "Fale-nos do seu negócio. Diremos exatamente o que é preciso para o tornar visível.",
      cta1: "Agendar chamada",
      cta2: "Começar um projeto",
    },
    marquee: ["Dubai", "Sharjah", "Deira", "Ser encontrado", "Ser entendido", "Ser escolhido", "PT · FR · EN"],
    footer: {
      tagline: "Sites e aplicações para negócios em Dubai, Sharjah, Deira e em todos os Emirados.",
      nav: "Navegação", services: "Setores", legalTitle: "Legal",
      legal: "A Bezalel Visionary opera através de uma entidade francesa registada. Está previsto um estabelecimento nos Emirados.",
      rights: "Todos os direitos reservados.",
      portal: "Área do cliente",
    },
    contact: {
      eyebrow: "Contacto",
      title: "Vamos tornar o seu negócio visível.",
      sub: "Uma chamada curta é a forma mais rápida de saber o que o seu negócio precisa. Sem compromisso, sem jargão.",
      name: "O seu nome", businessName: "Nome do negócio", email: "Email", phone: "Telefone / WhatsApp",
      businessType: "Tipo de negócio", location: "Localização",
      websiteStatus: "Site atual", needs: "Do que precisa?",
      preferredLanguage: "Idioma preferido", message: "Mensagem",
      messagePh: "Fale-nos brevemente do seu negócio…",
      businessTypes: ["Restaurante / Café", "Bem-estar / Saúde", "Hotel / Hospitalidade", "Vida noturna", "Negócio independente", "Outro"],
      locations: ["Dubai", "Sharjah", "Deira", "Abu Dhabi", "Outros Emirados", "Fora dos Emirados"],
      websiteOptions: ["Ainda sem site", "Site desatualizado", "Site que não consigo gerir", "Site funcional, quero mais"],
      needsOptions: ["Site", "Aplicação web / mobile", "Presença digital", "Ainda não sei"],
      submit: "Agendar chamada",
      sending: "A enviar…",
      successTitle: "Obrigado — recebemos o seu pedido.",
      successBody: "Respondemos dentro de um dia útil, no seu idioma.",
      another: "Enviar outro pedido",
      required: "Indique o seu nome e um email válido.",
      error: "Algo correu mal. Tente novamente.",
      select: "Selecionar…",
    },
    login: {
      clientTitle: "Área do cliente",
      adminTitle: "Portal admin",
      sub: "Inicie sessão para acompanhar o seu projeto, faturas e pedidos.",
      adminSub: "Acesso restrito.",
      email: "Email", password: "Palavra-passe", submit: "Entrar", signingIn: "A entrar…",
      demo: "Acesso demo: client@demo.com / Client@Demo2026",
      back: "Voltar ao site",
    },
    portal: {
      welcome: "Bem-vindo de volta",
      project: "Estado do projeto",
      stages: { concept: "Concept", revision: "Révision", validation: "Validation", delivered: "Livré" },
      updated: "Última atualização",
      visibility: "Visibilidade",
      visibilityEmpty: "As estatísticas de visibilidade aprovadas aparecerão aqui quando verificadas. Só mostramos números reais.",
      finance: "Finanças",
      invoice: "Fatura", amount: "Valor", status: "Estado", due: "Vencimento",
      paid: "Paga", dueStatus: "A pagar",
      requests: "Pedidos & revisões",
      newRequest: "Novo pedido",
      kind: "Tipo", request: "Pedido", revision: "Revisão",
      subject: "Assunto", messageLabel: "Mensagem", send: "Enviar", cancel: "Cancelar",
      open: "Aberto", inProgress: "Em curso", resolved: "Resolvido",
      empty: "Ainda sem pedidos. Precisa de uma alteração? Envie-nos um pedido.",
      logout: "Terminar sessão",
      requestSent: "Pedido enviado — tratamos do resto.",
    },
    admin: {
      title: "Painel admin",
      overview: "Visão geral",
      stats: { new_enquiries: "Novos pedidos", clients: "Clientes", active_projects: "Projetos ativos", open_requests: "Pedidos abertos" },
      tabs: { enquiries: "Pedidos", clients: "Clientes & projetos", requests: "Solicitações" },
      enquiryCols: { name: "Nome", business: "Negócio", contact: "Contacto", type: "Tipo", lang: "Idioma", status: "Estado", received: "Recebido" },
      statuses: { new: "Novo", contacted: "Contactado", closed: "Fechado" },
      projectStage: "Etapa do projeto",
      noEnquiries: "Ainda sem pedidos.",
      logout: "Terminar sessão",
    },
    login2: { welcome: "Bem-vindo de volta", subtitle: "Aceda ao seu espaço de cliente Bezalel Visionary.", google: "Continuar com Google", or: "ou", magic: "Enviar-me um link de acesso", magicSent: "Verifique a sua caixa de entrada — o seu link seguro está a caminho.", magicInvalid: "Este link de acesso é inválido ou expirou.", usePassword: "Usar email & palavra-passe", useMagic: "Usar um link de acesso", forgot: "Esqueceu a palavra-passe?", resetSent: "Se este email existir, foi enviado um link de redefinição.", resetTitle: "Escolha uma nova palavra-passe", newPassword: "Nova palavra-passe (mín. 8 caracteres)", resetSubmit: "Redefinir", resetDone: "Palavra-passe atualizada — já pode entrar.", emailPh: "voce@negocio.com" },
    payment: { checking: "A confirmar o pagamento…", success: "Pagamento confirmado — obrigado!", receipt: "A sua fatura está agora marcada como paga na secção Finanças.", failed: "Não foi possível confirmar este pagamento. Se foi debitado, o estado será atualizado em breve.", back: "Voltar à área do cliente" },
    work: { eyebrow: "Trabalhos selecionados", title: "Trabalho que torna negócios encontráveis.", sub: "Projetos reais que desenhámos e desenvolvemos — plataformas, aplicações web e sites.", demoTag: "Demonstração", visit: "Ver o site", back: "Todos os projetos" },
    blog: { eyebrow: "Journal", title: "Notícias & dicas", sub: "Notas práticas sobre visibilidade, sites e presença digital para negócios nos Emirados.", readMore: "Ler o artigo", back: "Todos os artigos", empty: "Ainda sem artigos publicados." },
    faqT: { faqTitle: "Perguntas frequentes", testimonialsTitle: "O que dizem os clientes", testimonialsEmpty: "Testemunhos verificados aparecerão aqui à medida que os projetos forem entregues — nunca publicamos avaliações inventadas." },
    portal2: { messages: "Mensagens", noMessages: "Ainda sem mensagens. Escreva-nos abaixo.", typeMessage: "Escrever uma mensagem…", send: "Enviar", deliverables: "Entregáveis & ficheiros", noDeliverables: "Os entregáveis aparecerão aqui assim que estiverem prontos.", updates: "Atualizações importantes", payNow: "Pagar", receipt: "Recibo (PDF)", contactTitle: "Contacto & reuniões", contactBody: "Precisa de falar sobre o seu projeto? Contacte-nos diretamente — respondemos no seu idioma.", emailUs: "Enviar email", whatsapp: "WhatsApp", open: "Abrir" },
    misc: { loading: "A carregar…", flagLogo: "Marca de navegação simplificada pendente do asset oficial." },
  },
};
